import { InputSummaryItem } from '../../../../input-summary';
import InputSummaryUtils from '../../../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../../../data-structures/unit-value.model';

/**
 * An interface used to define the class structure for the feedstock bmp potential yield model
 */
export interface FeedstockBmpPotentialYieldInterface {
  yieldType: string;
  yield: UnitValue;
  ch4Concentration: UnitValue;
  period: UnitValue;
}

/**
 * A class that contains members for the feedstock BMP potential yield model
 */
export class FeedstockBmpPotentialYield {
  yieldType: string;
  yield: UnitValue;
  ch4Concentration: UnitValue;
  period: UnitValue;

  /**
   * constructs the FeedstockBmpPotentialYield class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockBmpPotentialYieldInterface = {
    yieldType: BmpYieldType.BIOGAS_YIELD,
    yield: new UnitValue({value: null, unit: '000000000000000000004207'}),
    ch4Concentration: new UnitValue(),
    period: new UnitValue({value: null, unit: '000000000000000000001302'}),
  }) {
    this.yieldType = param.yieldType;
    this.yield = new UnitValue(param.yield);
    this.ch4Concentration = new UnitValue(param.ch4Concentration);
    this.period = new UnitValue(param.period);
  }

  /**
   * returns a separate instance of the provided FeedstockBmpPotentialYield obj
   * @param feedstockBmpPotentialYield will be returned as a separate instance
   */
  static deepCopy(feedstockBmpPotentialYield: FeedstockBmpPotentialYield) {
    let copy;
    if (feedstockBmpPotentialYield) {
      copy = new FeedstockBmpPotentialYield(feedstockBmpPotentialYield);
      return copy;
    }
    throw new Error('unable to perform deep copy of FeedstockBmpPotentialYield');
  }

  /**
   * Returns an array of InputSummaryItem containing inputs for each member of the FeedstockBmpPotentialYield class
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialYieldPropName.yieldType, this.yieldType, [...path, 'yieldType']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialYieldPropName.yield, this.yield, [...path, 'yield']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialYieldPropName.ch4Concentration, this.ch4Concentration, [...path, 'ch4Concentration']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialYieldPropName.period, this.period, [...path, 'period']);
    return inputs;
  }
}

/**
 * An enum that contains a proper display name for properties of the FeedstockBmpPotentialYield class.
 *
 * ALL MEMBERS ARE INTENTIONALLY CAMELCASE FOR OBJ REFERENCING
 */
export enum FeedstockBmpPotentialYieldPropName {
  yieldType = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.YIELD.YIELD_TYPE',
  yield = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.YIELD.YIELD',
  ch4Concentration = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.CH4_CONCENTRATION',
  period = 'PROJECT.FEEDSTOCK.BMP.DURATION',
  id_yieldType = 'yield type',
  id_yield = 'yield',
  id_ch4Concentration = 'CH₄ concentration',
  id_period = 'period',
}

/**
 * An enum that contains the types for BMP Yield
 */
export enum BmpYieldType {
  BIOGAS_YIELD = 'PROJECT.FEEDSTOCK.BMP.YIELD_TYPES.BIOGAS',
  BIOMETH_YIELD = 'PROJECT.FEEDSTOCK.BMP.YIELD_TYPES.BIOMETHANE'
}
